import { useQuery } from '@tanstack/react-query';
import { apiGet } from '@/lib/api/client.js';
import { useReportFilters } from '@/pages/reports/report-filters-context.js';

type WalkInQueueReport = {
  waitByHour: Array<{ hour: number; avgWaitMinutes: number | null; count: number }>;
  waitByDayOfWeek: Array<{ dayOfWeek: number; avgWaitMinutes: number | null; count: number }>;
  byReason: Array<{ reason: string; count: number }>;
  byStaff: Array<{
    userId: string | null;
    name: string;
    avgServiceMinutes: number | null;
    completed: number;
  }>;
  peakQueueDepthEstimate: number;
  totalEntries: number;
  abandonedCount: number;
  completedCount: number;
  abandonRate: number;
  statusCounts: Record<string, number>;
};

const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export function WalkInQueueReportPage() {
  const f = useReportFilters();
  const q = useQuery({
    queryKey: ['walk-in-queue-report', f.period, f.storeId, f.dateFrom, f.dateTo],
    queryFn: () =>
      apiGet<WalkInQueueReport>('/api/v1/reports/walk-in-queue', {
        params: {
          period: f.period,
          storeId: f.storeId || undefined,
          dateFrom: f.dateFrom || undefined,
          dateTo: f.dateTo || undefined,
        },
      }).then((r) => r.data),
  });

  const data = q.data;

  if (q.isLoading) return <p className="text-slate-600">Loading…</p>;
  if (q.isError || !data) return <p className="text-red-600">Could not load queue report.</p>;

  const maxHour = Math.max(1, ...data.waitByHour.map((h) => h.avgWaitMinutes ?? 0));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-950">Walk-in queue</h1>
        <p className="text-sm text-slate-500">
          Wait times for people in the lobby — primary staffing signal
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat
          label="Total check-ins"
          value={String(data.totalEntries)}
        />
        <Stat
          label="Completed"
          value={String(data.completedCount)}
        />
        <Stat
          label="Abandoned"
          value={`${data.abandonedCount} (${Math.round(data.abandonRate * 100)}%)`}
        />
        <Stat label="Peak queue # (day)" value={String(data.peakQueueDepthEstimate)} />
      </div>

      <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="text-lg font-bold text-slate-950">Average wait by hour of day</h2>
        <p className="mt-1 text-sm text-slate-500">Minutes from queue join to being called / leaving</p>
        <div className="mt-6 flex h-48 items-end gap-1">
          {Array.from({ length: 24 }, (_, hour) => {
            const row = data.waitByHour.find((h) => h.hour === hour);
            const v = row?.avgWaitMinutes ?? 0;
            const hPct = maxHour > 0 ? (v / maxHour) * 100 : 0;
            return (
              <div key={hour} className="flex flex-1 flex-col items-center gap-1">
                <div
                  className="w-full rounded-t bg-primary/80"
                  style={{ height: `${Math.max(hPct, v > 0 ? 4 : 0)}%` }}
                  title={row ? `${v} min · ${row.count} visits` : '—'}
                />
                <span className="text-[10px] text-slate-500">{hour}</span>
              </div>
            );
          })}
        </div>
      </section>

      <div className="grid gap-4 lg:grid-cols-2">
        <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="text-lg font-bold text-slate-950">Wait by day of week</h2>
          <table className="mt-4 w-full text-sm">
            <thead>
              <tr className="text-left text-slate-500">
                <th className="pb-2">Day</th>
                <th className="pb-2">Avg wait</th>
                <th className="pb-2">Visits</th>
              </tr>
            </thead>
            <tbody>
              {DOW.map((label, i) => {
                const row = data.waitByDayOfWeek.find((d) => d.dayOfWeek === i);
                return (
                  <tr key={label} className="border-t border-slate-100">
                    <td className="py-2 font-medium">{label}</td>
                    <td className="py-2">
                      {row?.avgWaitMinutes != null ? `${row.avgWaitMinutes} min` : '—'}
                    </td>
                    <td className="py-2">{row?.count ?? 0}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </section>

        <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="text-lg font-bold text-slate-950">Volume by reason</h2>
          <ul className="mt-4 space-y-2 text-sm">
            {data.byReason.length === 0 ? (
              <li className="text-slate-500">No data</li>
            ) : (
              data.byReason.map((r) => (
                <li key={r.reason} className="flex justify-between border-b border-slate-50 py-2">
                  <span className="capitalize">{r.reason.replaceAll('_', ' ')}</span>
                  <span className="font-semibold">{r.count}</span>
                </li>
              ))
            )}
          </ul>
        </section>
      </div>

      <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="text-lg font-bold text-slate-950">Average service duration by staff</h2>
        <table className="mt-4 w-full text-sm">
          <thead>
            <tr className="text-left text-slate-500">
              <th className="pb-2">Staff</th>
              <th className="pb-2">Avg service</th>
              <th className="pb-2">Completed</th>
            </tr>
          </thead>
          <tbody>
            {data.byStaff.length === 0 ? (
              <tr>
                <td colSpan={3} className="py-3 text-slate-500">
                  No completed queue visits in range
                </td>
              </tr>
            ) : (
              data.byStaff.map((s) => (
                <tr key={s.userId ?? s.name} className="border-t border-slate-100">
                  <td className="py-2 font-medium">{s.name}</td>
                  <td className="py-2">
                    {s.avgServiceMinutes != null ? `${s.avgServiceMinutes} min` : '—'}
                  </td>
                  <td className="py-2">{s.completed}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </section>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-1 text-2xl font-bold text-slate-950">{value}</p>
    </div>
  );
}
